import { IconButton } from '@mui/material';
import { GridColDef, getGridStringOperators } from '@mui/x-data-grid-pro';

import { IcDotSpread } from '@/assets/images';
import { excludeValues } from '@/components/DataGrid/utils';
import { IMasterNameOrganization, OrganizationType } from '@/models';
import { PhoneNumbersCell } from '@/pages/MasterIndex/components/PhoneNumbersCell';
import { getAddressLocation } from '@/services';
import { generateUniqueId, getUpdatedLabel } from '@/utils';

const uniqueId = (): Partial<GridColDef> => ({
  renderCell: (params) => {
    const { uniqueId: uId } = params.row;
    return generateUniqueId('ORG', uId);
  },
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isNotEmpty', 'isEmpty', 'isAnyOf']),
  ),
  valueGetter: (_, row) => generateUniqueId('ORG', row.uniqueId),
});

const name = (): Partial<GridColDef> => ({
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isAnyOf', 'isNotEmpty', 'isEmpty']),
  ),
});

const address = (): Partial<GridColDef> => ({
  field: 'address',
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isAnyOf', 'equals', 'doesNotEqual']),
  ),
  valueGetter: (_, row) => {
    return row.address ? getAddressLocation(row.address) : '/';
  },
});

const organizationType = (): Partial<GridColDef> => ({
  renderCell: (params) =>
    params.row.organizationType
      ? OrganizationType[
          params.row.organizationType as keyof typeof OrganizationType
        ]
      : '/',
  filterable: false,
});

const subAddress = (): Partial<GridColDef> => ({
  field: 'address.subAddress',
  renderCell: (params) => params.row.address?.subAddress || '/',
  valueGetter: (_, row) => row?.address?.subAddress,
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isAnyOf', 'equals', 'doesNotEqual']),
  ),
});

const phoneNumbers = (): Partial<GridColDef> => ({
  renderCell: (params) => (
    <PhoneNumbersCell phoneNumbers={params.row.phoneNumbers} />
  ),
  sortable: false,
  filterable: false,
  valueGetter: (_, row) => row.phoneNumbers?.[0]?.phoneNumber,
});

const lastUpdated = (): Partial<GridColDef> => ({
  renderCell: (params) => getUpdatedLabel(params.row?.updatedAt),
  sortable: false,
  filterable: false,
});

const actions = (
  openMenu?: (
    event: React.MouseEvent<HTMLButtonElement>,
    row: IMasterNameOrganization,
  ) => void,
): Partial<GridColDef> => ({
  width: 96,
  sortable: false,
  filterable: false,
  align: 'center',
  headerAlign: 'center',
  disableColumnMenu: true,
  renderCell: (params) => (
    <IconButton
      onClick={(event) => {
        event.stopPropagation();
        openMenu?.(event, params.row);
      }}
    >
      <IcDotSpread />
    </IconButton>
  ),
});

export const masterOrganizationCols = (
  _navigate?: (path: string) => void,
  _onDelete?: (row: IMasterNameOrganization) => void,
  openMenu?: (
    event: React.MouseEvent<HTMLButtonElement>,
    row: IMasterNameOrganization,
  ) => void,
) => {
  return {
    uniqueId,
    name,
    address,
    organizationType,
    subAddress,
    phoneNumbers,
    lastUpdated,
    actions: () => actions(openMenu),
  };
};
